import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import usePermissions from '../../../hooks/usePermissions';
import LogoLoader from '../../common/LogoLoader';
import Pagination from '../../common/Pagination';
import CreditDebitModal from './CreditDebitModal';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Button, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, InputAdornment, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import './CreditDebitIndex.css';

const CreditDebitIndex = () => {
  useSessionValidation();

  const { companyId, branchId, token, userId } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Accounts');

  // Modal
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Data
  const [records, setRecords] = useState([]);
  const [dropdownData, setDropdownData] = useState({ Party: [], Other: [], Bank: [] });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const getDefaultFromDate = () => {
    const t = new Date();
    return formatDateToYYYYMMDD(new Date(t.getFullYear(), t.getMonth(), 1));
  };
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(formatDateToYYYYMMDD(new Date()));

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading / feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirm
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (companyId && token) fetchDropdownData();
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId && token && fromDate && toDate) fetchRecords();
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecords = async () => {
    try {
      const res = await fetchData('CreditDebitApi', `/All/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      setRecords(res.response_data || res || []);
    } catch (err) {
      console.error('Error fetching credit/debit records:', err);
      setRecords([]);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [mastersRes, accountHeadsRes, banksRes] = await Promise.allSettled([
        fetchData('MasterApi', `/${companyId}`, token),
        fetchData('AccHeadApi', `/${companyId}`, token),
        fetchData('BankApi', `/${companyId}`, token),
      ]);
      const masters = mastersRes.status === 'fulfilled' ? (mastersRes.value.response_data || mastersRes.value || []) : [];
      const accountHeads = accountHeadsRes.status === 'fulfilled' ? (accountHeadsRes.value.response_data || accountHeadsRes.value || []) : [];
      const banks = banksRes.status === 'fulfilled' ? (banksRes.value.response_data || banksRes.value || []) : [];
      setDropdownData({ Party: masters, Other: accountHeads, Bank: banks });
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  };

  const fetchMaxBillNo = useCallback(async () => {
    try {
      const res = await fetchData('CreditDebitApi', `/MaxBillno/${companyId}/${companyId}/${branchId}`, token);
      const data = res.response_data || res || {};
      return { billNumber: data.BillNumber || data.billNumber || 1 };
    } catch {
      return { billNumber: 1 };
    }
  }, [companyId, branchId, token]);

  const handleOpenModal = useCallback(async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);
    if (action === 'create') {
      setInitialData(null);
    } else if (item) {
      setInitialData({ ...item, BillDate: item.BillDate ? item.BillDate.split('T')[0] : '' });
    }
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedItem(null);
    setInitialData(null);
  }, []);

  const handleSave = useCallback(async (formData) => {
    setModalLoading(true);
    try {
      const payload = {
        ...formData,
        CompanyId: companyId,
        BranchId: branchId,
        Amount: parseFloat(formData.Amount) || 0,
      };
      if (modalAction === 'create') {
        payload.CreatedBy = userId;
        payload.CreatedDate = new Date().toISOString();
        await postData('CreditDebitApi','', payload, token);
        setSuccess('Record created successfully!');
      } else {
        payload.ModifiedBy = userId;
        payload.ModifiedDate = new Date().toISOString();
        await updateData('CreditDebitApi', payload.Id, payload, token);
        setSuccess('Record updated successfully!');
      }
      handleCloseModal();
      await fetchRecords();
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save. Please try again.');
    }
    setModalLoading(false);
  }, [companyId, branchId, userId, modalAction, handleCloseModal, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    try {
      await deleteData('CreditDebitApi', deleteItemId, token);
      setSuccess('Record deleted successfully!');
      setDeleteModalOpen(false);
      await fetchRecords();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete. Please try again.');
    }
  };

  const formatCurrency = (amount) =>
    `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getAccountName = (id, acType) => {
    const list = acType === 1 ? dropdownData.Party : acType === 2 ? dropdownData.Other : dropdownData.Bank;
    return list.find(m => m.Id === id)?.Name || '-';
  };

  // Filter
  const filteredData = useMemo(() => records.filter(item => {
    const term = searchTerm.toLowerCase();
    return !term ||
      String(item.BillNo).toLowerCase().includes(term) ||
      (item.Remarks || '').toLowerCase().includes(term);
  }), [records, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = useMemo(
    () => filteredData.slice(startIndex, startIndex + rowsPerPage),
    [filteredData, startIndex, rowsPerPage]
  );

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Credit / Debit..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Roman' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>💳 Credit / Debit</span>
            </h4>
          </div>

          {/* Filters + Search + Create */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date" size="small" value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date" size="small" value={toDate}
                onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <TextField
              fullWidth size="small" placeholder="Search by bill no or remarks..." value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            <Tooltip title="Create a new credit/debit entry" arrow>
              {canCreate && (
              <Button variant="contained" onClick={() => handleOpenModal('create')}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '14px' }}>+</span> Create
              </Button>
              )}
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 650 }} aria-label="credit debit table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Bill No.', 'Date', 'Type', 'Credit Account', 'Debit Account', 'Amount', 'Remarks', 'Actions'].map((h, i) => (
                      <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => {
                      const creditId = item.CreditMasterId || item.CreditAccountHeadId || item.CreditBankId;
                      const debitId = item.DebitMasterId || item.DebitAccountHeadId || item.DebitBankId;
                      const creditName = getAccountName(creditId, item.AcTypeCredit);
                      const debitName = getAccountName(debitId, item.AcTypeDebit);

                      return (
                        <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                            {item.BillNo}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {formatDate(item.BillDate)}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                            <Chip
                              label={item.IsTypeCredit ? 'Credit' : 'Debit'}
                              size="small"
                              style={{ fontSize: '10px', height: '18px', background: item.IsTypeCredit ? '#d1fae5' : '#fee2e2', color: item.IsTypeCredit ? '#065f46' : '#dc2626', fontWeight: '600' }}
                            />
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '500' }}>
                            {creditName}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '500' }}>
                            {debitName}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2e7d32', fontWeight: '600' }}>
                            {formatCurrency(item.Amount)}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.Remarks || '-'}
                          </TableCell>
                          <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <Tooltip title="View Details" arrow>
                                <Button size="small" onClick={() => handleOpenModal('view', item)}
                                  style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
                                  👁️
                                </Button>
                              </Tooltip>

                              {canEdit && (
                              <Tooltip title="Edit" arrow>
                                <Button size="small" onClick={() => handleOpenModal('edit', item)}
                                  style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(245,158,11,0.3)' }}>
                                  ✏️
                                </Button>
                              </Tooltip>
                              )}

                              {canDelete && (
                                <Tooltip title="Delete" arrow>
                                  <Button size="small"
                                    onClick={() => { setDeleteItemId(item.Id); setDeleteItemNo(item.BillNo); setDeleteModalOpen(true); }}
                                    style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(255,107,107,0.3)' }}>
                                    🗑️
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newSize) => { setRowsPerPage(newSize); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to delete Bill No. <strong>{deleteItemNo}</strong>? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setDeleteModalOpen(false)}
              style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleDelete}
              style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal */}
        <CreditDebitModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSave}
          modalAction={modalAction}
          initialData={initialData}
          dropdownData={dropdownData}
          saving={modalLoading}
          fetchMaxBillNo={fetchMaxBillNo}
        />

        {/* Snackbars */}
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default CreditDebitIndex;
